import moment from "moment";
import { useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import {
    Autocomplete, AutocompleteAPI, Box,
    ControlLabelCheckBox, DateTimePicker,
    RadioGroup, Typography,
    useCommonComponentContext
} from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import ModalAddTag from "./ModalAddTag";
import useDataCommon from "hooks/useDataCommon.hook";

import { BooleanInt, Mode, RepeatIssue, ReportStatus } from "constants/enum";
import { ICodename, IDataDetailReport, IErrorReport, IRecordTag } from "commons/interfaces";


const CardGeneralInfo = ({
    mode,
    data,
    error,
    dataOptions,
    handleChangeValue,
    onCallBackModalAddTag,
}: {
    mode: Mode,
    error: IErrorReport;
    data: IDataDetailReport;
    onCallBackModalAddTag: (item: IRecordTag) => void;
    handleChangeValue: (key: string, value: any) => void;
    dataOptions: {
        tags: ICodename[];
        reportType: ICodename[];
        incidentObject: ICodename[];
    };
}) => {

    const { getDataReferenceReports } = useDataCommon();
    const [openModalAddTag, setOpenModalAddTag] = useState(false);

    const { userInfo } = useCommonComponentContext();
    const reportTypeDisabled: boolean = data?.anonymousReporter === BooleanInt.True && data?.createUser !== userInfo?.userProfile?.identityId;

    return (
        <Card sx={{ padding: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {Strings.REPORT.REPORTER}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ gap: 1, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="button" sx={{ fontSize: "0.785rem", lineHeight: 1.5 }} color="secondary">
                            {Strings.REPORT.REPORT_TYPE}:
                        </Typography>
                        <RadioGroup
                            isRow
                            label={""}
                            disabled={mode === Mode.View || reportTypeDisabled}
                            value={`${data?.selectedType}`}
                            dataMulti={dataOptions.reportType}
                            onChangeValue={(value) => {
                                handleChangeValue("selectedType", `${value}`);
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimePicker
                        mode={mode}
                        variant={"outlined"}
                        errorMessage={error?.createTime}
                        value={Helpers.toUnix(data?.createTime)}
                        label={Strings.REPORT.CREATE_TIME_REPORT}
                        onChangeValue={(value) => {
                            const newValue = value ? moment(value).unix() : undefined;
                            handleChangeValue("createTime", newValue);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimePicker
                        mode={mode}
                        variant={"outlined"}
                        errorMessage={error?.reportTime}
                        label={Strings.REPORT.INCIDENT_TIME}
                        value={Helpers.toUnix(data?.reportTime)}
                        onChangeValue={(value) => {
                            const newValue = value ? moment(value).unix() : undefined;
                            handleChangeValue("reportTime", newValue);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        mode={mode}
                        variant={"outlined"}
                        data={dataOptions.incidentObject}
                        defaultValue={data?.selectedObjects}
                        label={Strings.REPORT.INCIDENT_OBJECT}
                        placeholder={Strings.REPORT.INCIDENT_OBJECT}
                        onChange={(value) => {
                            handleChangeValue("selectedObjects", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <ControlLabelCheckBox
                        mode={mode}
                        disabled={mode === Mode.View ||  data?.reportStatus >= ReportStatus.analyzed}
                        label={Strings.REPORT.REPEAT_ISSUE}
                        value={data?.repeatIssue === RepeatIssue.RepeatedUnconfirmed || data?.repeatIssue === RepeatIssue.Repeated}
                        onChangeValue={() => {
                            if (data?.repeatIssue === RepeatIssue.RepeatedUnconfirmed) {
                                handleChangeValue("repeatIssue", RepeatIssue.None);
                            } else {
                                handleChangeValue("repeatIssue", RepeatIssue.RepeatedUnconfirmed);
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AutocompleteAPI
                        mode={mode}
                        variant={"outlined"}
                        defaultValue={data?.referenceId}
                        errorMessage={error?.referenceId}
                        optionDisabled={[data?.id || ""]}
                        label={Strings.REPORT.REFERENCE_INCIDENT}
                        required={data?.repeatIssue === BooleanInt.True}
                        disabled={data?.repeatIssue !== BooleanInt.True}
                        placeholder={Strings.REPORT.SELECT_REFERENCE_INCIDENT}
                        dataSelected={data?.referenceId ? [{
                            code: data?.referenceId || "",
                            name: data?.referenceCode || "",
                        }] : []}
                        requestGetApi={{ repeatIssues: [BooleanInt.False] }}
                        getApi={getDataReferenceReports}
                        onChange={(value) => { }}
                        onChangeItem={(value) => {
                            handleChangeValue("referenceId", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Box sx={{
                        gap: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <Box sx={{ width: "calc(100% - 128px)" }}>
                            <Autocomplete
                                multiple
                                mode={mode}
                                variant={"outlined"}
                                data={dataOptions.tags}
                                label={Strings.REPORT.TAG}
                                defaultValue={data?.tagIds || []}
                                errorMessage={error?.byHospitalSource}
                                placeholder={Strings.REPORT.SELECT_TAG}
                                onChange={(val) => {
                                    handleChangeValue("tagIds", val || []);
                                }}
                            />
                        </Box>
                        <Box onClick={(e) => {
                            e.stopPropagation();
                            setOpenModalAddTag(true);
                        }}
                            sx={{
                                width: "120px",
                                ".MuiTypography-root": {
                                    textDecoration: "underline",
                                },
                                ":hover": {
                                    cursor: "pointer",
                                    ".MuiTypography-root": {
                                        fontWeight: "bold",
                                    },
                                }
                            }}
                        >
                            <Typography variant="button" color="info">
                                {Strings.REPORT.ADD_NEW_TAG}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            {openModalAddTag &&
                <ModalAddTag
                    visible={openModalAddTag}
                    setVisible={setOpenModalAddTag}
                    onCallBack={onCallBackModalAddTag}
                />
            }
        </Card>
    );
}

export default CardGeneralInfo;